import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Trophy } from 'lucide-react';
import DateRangeBar from './DateRangeBar';
import { queryDateParams, rangeForPreset, rangeSpanDays } from '../utils/dateRange';
import './Statistics.css';

function Statistics({ token, nightMode, onTeamClick, onMatchClick }) {
  const initialRange = rangeForPreset('year');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [range, setRange] = useState(initialRange);
  const [teamFilter, setTeamFilter] = useState('');

  useEffect(() => {
    fetchStatistics();
  }, [range.dateFrom, range.dateTo]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/matches/statistics`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryDateParams(range.dateFrom, range.dateTo)
      });
      setStats(response.data);
      setLoading(false);
    } catch (err) {
      setError('Couldn’t load stats. ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const formatMatchTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  };

  const openTeam = (name) => {
    if (onTeamClick) onTeamClick(name, range);
  };

  const showGames = rangeSpanDays(range.dateFrom, range.dateTo) <= 14;
  const o = stats?.overall;
  const hasStats = Boolean(o);
  const recordTotal = ((o?.wins || 0) + (o?.losses || 0) + (o?.ties || 0)) || 1;
  const form = (o?.recentForm || '').split('').filter(Boolean);
  const topTeams = (stats?.teams || []).slice(0, 6);
  const dayMatches = stats?.matches || [];

  return (
    <div className={`statistics ${nightMode ? 'night-mode' : ''}`}>
      <div className="stats-toolbar">
        <div className="stats-toolbar-left">
          <h1>Stats</h1>
        </div>
      </div>

      <DateRangeBar
        preset={range.preset}
        dateFrom={range.dateFrom}
        dateTo={range.dateTo}
        onChange={setRange}
      />

      {loading && <div className="loading">Loading stats…</div>}

      {!loading && error && (
        <div className="error-message">
          {error}
          <button onClick={fetchStatistics} className="retry-btn">Try again</button>
        </div>
      )}

      {!loading && !error && !hasStats && (
        <div className="empty-state">
          <Trophy size={32} />
          <p>No matches in this range yet.</p>
        </div>
      )}

      {!loading && !error && hasStats && (
        <>
          <nav className="stats-tabs" aria-label="Stats sections">
            <button type="button" className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>Overview</button>
            <button type="button" className={activeTab === 'games' ? 'active' : ''} onClick={() => setActiveTab('games')}>Games</button>
            <button type="button" className={activeTab === 'teams' ? 'active' : ''} onClick={() => setActiveTab('teams')}>Teams</button>
            <button type="button" className={activeTab === 'opponents' ? 'active' : ''} onClick={() => setActiveTab('opponents')}>Opponents</button>
          </nav>

          {activeTab === 'overview' && (
            <div className="overview-tab">
              <section className="record-card">
                <div className="record-line">
                  <div className="record-num">
                    <strong>{o.wins || 0}</strong>
                    <span>W</span>
                  </div>
                  <span className="record-sep">–</span>
                  <div className="record-num">
                    <strong>{o.losses || 0}</strong>
                    <span>L</span>
                  </div>
                  <span className="record-sep">–</span>
                  <div className="record-num">
                    <strong>{o.ties || 0}</strong>
                    <span>T</span>
                  </div>
                  <div className="record-pct">
                    <strong>{o.winPercentage || 0}%</strong>
                    <span>win rate</span>
                  </div>
                </div>
                <div className="record-bar" aria-hidden="true">
                  <span className="seg win" style={{ width: `${((o.wins || 0) / recordTotal) * 100}%` }} />
                  <span className="seg loss" style={{ width: `${((o.losses || 0) / recordTotal) * 100}%` }} />
                  <span className="seg tie" style={{ width: `${((o.ties || 0) / recordTotal) * 100}%` }} />
                </div>
              </section>

              {form.length > 0 && (
                <section className="form-strip">
                  <span className="form-label">Form</span>
                  <div className="form-display">
                    {form.map((result, index) => (
                      <span key={index} className={`form-dot ${result === 'W' ? 'form-win' : result === 'L' ? 'form-loss' : 'form-tie'}`}>
                        {result}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              <div className="metric-strip">
                <div>
                  <strong>{o.totalMatches}</strong>
                  <span>Matches</span>
                </div>
                <div>
                  <strong>{o.avgPointsFor}</strong>
                  <span>Pts for</span>
                </div>
                <div>
                  <strong>{o.avgPointsAgainst}</strong>
                  <span>Pts against</span>
                </div>
                <div>
                  <strong>{formatTime(o.totalTime)}</strong>
                  <span>Time</span>
                </div>
              </div>

              {showGames && dayMatches.length > 0 && (
                <section className="list-section">
                  <h2>Games</h2>
                  <MatchList
                    matches={dayMatches}
                    formatMatchTime={formatMatchTime}
                    onMatchClick={onMatchClick}
                    onTeamClick={openTeam}
                  />
                </section>
              )}

              {topTeams.length > 0 && (
                <section className="list-section">
                  <h2>Teams</h2>
                  <div className="rank-list">
                    {topTeams.map((team) => (
                      <StatRow
                        key={team.name}
                        name={team.name}
                        wins={team.wins}
                        losses={team.losses}
                        ties={team.ties}
                        pct={parseFloat(team.winPercentage) || 0}
                        onClick={() => openTeam(team.name)}
                      />
                    ))}
                  </div>
                </section>
              )}

              {(stats.matchTypes || []).length > 0 && (
                <section className="list-section">
                  <h2>By type</h2>
                  <div className="rank-list">
                    {stats.matchTypes.map((type) => (
                      <StatRow
                        key={type.type}
                        name={type.type}
                        wins={type.wins}
                        losses={type.losses}
                        ties={type.ties}
                        pct={parseFloat(type.winPercentage) || 0}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}

          {activeTab === 'games' && (
            <div className="list-section">
              {dayMatches.length === 0 ? (
                <p className="empty-inline">No games in this range.</p>
              ) : (
                <MatchList
                  matches={dayMatches}
                  formatMatchTime={formatMatchTime}
                  onMatchClick={onMatchClick}
                  onTeamClick={openTeam}
                />
              )}
            </div>
          )}

          {activeTab === 'teams' && (
            <div className="list-section">
              {(stats.teams || []).length === 0 ? (
                <p className="empty-inline">No team stats in this range.</p>
              ) : (
                <>
                  <input
                    className="stats-search"
                    type="search"
                    placeholder="Search teams…"
                    value={teamFilter}
                    onChange={(e) => setTeamFilter(e.target.value)}
                  />
                  <div className="rank-list">
                    {stats.teams
                      .filter((team) => team.name.toLowerCase().includes(teamFilter.trim().toLowerCase()))
                      .map((team) => {
                        const diff = (team.pointsFor || 0) - (team.pointsAgainst || 0);
                        return (
                          <StatRow
                            key={team.name}
                            name={team.name}
                            wins={team.wins}
                            losses={team.losses}
                            ties={team.ties}
                            pct={parseFloat(team.winPercentage) || 0}
                            meta={`${team.matches} sets · ${diff >= 0 ? '+' : ''}${diff} pts`}
                            onClick={() => openTeam(team.name)}
                          />
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'opponents' && (
            <div className="list-section">
              {(stats.opponents || []).length === 0 ? (
                <p className="empty-inline">No opponent stats in this range.</p>
              ) : (
                <div className="rank-list">
                  {stats.opponents.map((opponent) => {
                    const yourWins = opponent.losses || 0;
                    const yourLosses = opponent.wins || 0;
                    const yourTies = opponent.ties || 0;
                    const pct = opponent.matches > 0
                      ? (yourWins / opponent.matches) * 100
                      : 0;
                    return (
                      <StatRow
                        key={opponent.name}
                        name={opponent.name}
                        wins={yourWins}
                        losses={yourLosses}
                        ties={yourTies}
                        pct={pct}
                        meta={`${opponent.matches} meetings`}
                        onClick={() => openTeam(opponent.name)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MatchList({ matches, formatMatchTime, onMatchClick, onTeamClick }) {
  return (
    <div className="stats-game-list">
      {matches.map((match) => {
        const result = match.scoreA > match.scoreB ? 'win' : match.scoreB > match.scoreA ? 'loss' : 'tie';
        return (
          <div key={match._id} className={`stats-game-row ${result}`}>
            <button
              type="button"
              className="stats-game-main"
              onClick={() => onMatchClick && onMatchClick(match)}
            >
              <span className="stats-game-time">
                <Calendar size={13} />
                {formatMatchTime(match.date)}
              </span>
              <span className="stats-game-teams">
                <span>{match.teamA}</span>
                <span className="stats-game-score">{match.scoreA}–{match.scoreB}</span>
                <span>{match.teamB}</span>
              </span>
            </button>
            {onTeamClick && (
              <div className="stats-game-links">
                <button type="button" onClick={() => onTeamClick(match.teamA)}>{match.teamA}</button>
                <button type="button" onClick={() => onTeamClick(match.teamB)}>{match.teamB}</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatRow({ name, wins, losses, ties, pct, meta, onClick }) {
  const record = ties
    ? `${wins}–${losses}–${ties}`
    : `${wins}–${losses}`;

  const inner = (
    <>
      <div className="stat-row-top">
        <span className="stat-row-name" title={name}>{name}</span>
        <span className="stat-row-record">{record}</span>
        <span className="stat-row-pct">{pct.toFixed(0)}%</span>
      </div>
      <div className="stat-row-bar" aria-hidden="true">
        <span style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
      </div>
      {meta && <span className="stat-row-meta">{meta}</span>}
    </>
  );

  if (onClick) {
    return (
      <button type="button" className="stat-row is-clickable" onClick={onClick}>
        {inner}
      </button>
    );
  }

  return <div className="stat-row">{inner}</div>;
}

export default Statistics;
